// App.js
import React, { useState, useEffect } from 'react';
import { Connection, PublicKey } from '@solana/web3.js';
import './App.css';

function App() {
  const [wallets, setWallets] = useState([]);
  const [newWallet, setNewWallet] = useState('');
  const [newWalletName, setNewWalletName] = useState('');  // Nouvel état
  const [isLoading, setIsLoading] = useState(false);
  const [rpcUrl, setRpcUrl] = useState('https://rpc.ironforge.network/mainnet?apiKey=01HRZ9G6Z2A19FY8PR4RF4J4PW');
  const [connection, setConnection] = useState(new Connection(rpcUrl));
  const [autoUpdate, setAutoUpdate] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('solanaWallets');
    const storedRpc = localStorage.getItem('solanaRpc');
    if (stored) {
      setWallets(JSON.parse(stored));
    }
    if (storedRpc) {
      setRpcUrl(storedRpc);
      setConnection(new Connection(storedRpc));
    }
  }, []);

  useEffect(() => {
    let interval;
    if (autoUpdate) {
      interval = setInterval(() => {
        updateWallets();
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [autoUpdate]);

  const handleRpcChange = (e) => {
    const newRpc = e.target.value;
    setRpcUrl(newRpc);
    setConnection(new Connection(newRpc));
    localStorage.setItem('solanaRpc', newRpc);
  };

  const addWallet = (e) => {
    e.preventDefault();
    try {
      new PublicKey(newWallet); // Validate address
      const newWallets = [...wallets, {
        address: newWallet,
        name: newWalletName || 'Unnamed',  // Utiliser 'Unnamed' si vide
        balance: null,
        lastTx: null
      }];
      setWallets(newWallets);
      localStorage.setItem('solanaWallets', JSON.stringify(newWallets));
      setNewWallet('');
      setNewWalletName('');
    } catch (error) {
      alert('Invalid Solana address');
    }
  };

  const removeWallet = (address) => {
    const newWallets = wallets.filter(w => w.address !== address);
    setWallets(newWallets);
    localStorage.setItem('solanaWallets', JSON.stringify(newWallets));
  };

  const updateWallets = async () => {
    setIsLoading(true);
    try {
      const updatedWallets = await Promise.all(
        wallets.map(async wallet => {
          try {
            const pubKey = new PublicKey(wallet.address);
            const [balance, signatures] = await Promise.all([
              connection.getBalance(pubKey),
              connection.getSignaturesForAddress(pubKey, { limit: 1 })
            ]);
            
            const newBalance = (balance / 1e9).toFixed(4);
            const newLastTx = signatures[0]?.blockTime 
              ? new Date(signatures[0].blockTime * 1000).toLocaleString() 
              : 'N/A';

            return {
              ...wallet,
              balance: newBalance,
              lastTx: newLastTx,
              isBalanceNew: newBalance !== wallet.balance,
              isLastTxNew: newLastTx !== wallet.lastTx
            };
          } catch (err) {
            return { ...wallet, balance: 'Error', lastTx: 'Error' };
          }
        })
      );
      setWallets(updatedWallets);
      localStorage.setItem('solanaWallets', JSON.stringify(updatedWallets));
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <h1>Solana Wallet Tracker</h1>
      
      <div className="rpc-form">
        <label>RPC URL:</label>
        <input
          type="text"
          value={rpcUrl}
          onChange={handleRpcChange}
          placeholder="Enter RPC URL"
          className="rpc-input"
        />
      </div>

      <form onSubmit={addWallet} className="add-form">
        <input
          value={newWalletName}
          onChange={(e) => setNewWalletName(e.target.value)}
          placeholder="Wallet name (optional)"
          className="name-input"
        />
        <input
          value={newWallet}
          onChange={(e) => setNewWallet(e.target.value)}
          placeholder="Enter Solana wallet address"
          required
        />
        <button type="submit">Add Wallet</button>
      </form>

      <div className="button-group">
        <button 
          onClick={updateWallets} 
          disabled={isLoading || wallets.length === 0}
          className="update-button"
        >
          {isLoading ? 'Updating...' : 'Update All'}
        </button>
        <button 
          onClick={() => setAutoUpdate(!autoUpdate)} 
          className={`auto-update-button ${autoUpdate ? 'active' : ''}`}
          disabled={wallets.length === 0}
        >
          {autoUpdate ? 'Stop Auto Update' : 'Start Auto Update'}
        </button>
      </div>

      <table className="wallet-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Address</th>
            <th>Balance (SOL)</th>
            <th>Last Transaction</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {wallets.map(wallet => (
            <tr key={wallet.address}>
              <td>
                <a 
                  href={`https://solscan.io/account/${wallet.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wallet-link"
                >
                  {wallet.name}
                </a>
              </td>
              <td>{wallet.address}</td>
              <td>
                {wallet.balance ?? 'N/A'}
                {wallet.isBalanceNew && <span className="new-indicator">NEW</span>}
              </td>
              <td>
                {wallet.lastTx ?? 'N/A'}
                {wallet.isLastTxNew && <span className="new-indicator">NEW</span>}
              </td>
              <td>
                <button 
                  onClick={() => removeWallet(wallet.address)}
                  className="remove-button"
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="documentation">
        <h2>Documentation</h2>
        <div className="doc-section">
          <h3>Features</h3>
          <ul>
            <li><strong>RPC URL:</strong> Set your custom Solana RPC endpoint</li>
            <li><strong>Add Wallet:</strong> Enter a name (optional) and a valid Solana address</li>
            <li><strong>Update:</strong> "Update All" button refreshes balances and latest transactions</li>
            <li><strong>Auto Update:</strong> Toggle automatic updates every 30 seconds</li>
            <li><strong>Change Indicators:</strong> "NEW" badge appears when balance or transaction time changes</li>
            <li><strong>Solscan Links:</strong> Click on wallet names to view details on Solscan explorer</li>
          </ul>
        </div>
        <div className="doc-section">
          <h3>Storage</h3>
          <ul>
            <li>Wallets and RPC URL are saved in localStorage</li>
            <li>Data persists after page reload or browser close</li>
            <li><b>/!\ if you clean local storage all is lost /!\</b></li>
          </ul>
        </div>
        <div className="doc-section">
          <h3>Data Display</h3>
          <ul>
            <li><strong>Name:</strong> Custom identifier for the wallet (clickable link to Solscan)</li>
            <li><strong>Address:</strong> Solana public key</li>
            <li><strong>Balance:</strong> Current SOL amount (with change indicator)</li>
            <li><strong>Last Transaction:</strong> Timestamp of most recent activity (with change indicator)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default App;